import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {ProductOverviewSliceDefaultPrimaryProductSectionsItem} from 'prismicio-types'
import {EVENTS, track} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'

const GRID_CELL_CLASSES = [
  'group/card col-span-1 col-start-2 row-start-2 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 md:col-start-3 lg:col-start-2 xl:col-start-3',
  'group/card col-span-1 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-4 md:row-start-2 md:max-lg:col-start-5 lg:col-start-4 xl:col-start-5',
  'group/card col-span-1 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-6 md:max-lg:col-start-3 md:max-lg:row-start-4 lg:col-start-6 lg:row-start-2 xl:col-start-7',
  'group/card col-span-1 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-8 md:max-lg:col-start-5 md:max-lg:row-start-4 lg:col-start-2 lg:row-start-4 xl:col-start-3',
  'group/card col-span-1 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-10 md:max-lg:col-start-3 md:max-lg:row-start-6 lg:col-start-4 lg:row-start-4 xl:col-start-5',
  'group/card col-span-1 ml-[-1px] mt-[-1px] overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-12 md:max-lg:col-start-5 md:max-lg:row-start-6 lg:col-start-6 lg:row-start-4 xl:col-start-7',
]

type ProductOverviewCardProps = {
  content: ProductOverviewSliceDefaultPrimaryProductSectionsItem
  index: number
  componentIndex: number
  pageUid: string
}

export const ProductOverviewCard = ({content, index, componentIndex, pageUid}: ProductOverviewCardProps) => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()

  return (
    <Link
      href={linkResolver(content.link)}
      className={GRID_CELL_CLASSES[index]}
      onClick={() => {
        track({
          event: EVENTS.CLICK,
          mktgId: 'product-overview-click',
          location: `component-${componentIndex}-product-overview-${index}`,
          pageName: pageUid,
          dataType: 'product-overview-card',
        })

        trackMossAnalytics({
          event: 'interaction',
          interaction: {
            element: 'product-overview-card',
            type: 'click',
            elementText: content.product_title || '',
            location: `component-${componentIndex}-product-overview-${index}`,
          },
          page: {
            domainCountry,
            type: pageUid,
          },
        })
      }}
    >
      <div className="flex size-full flex-col justify-between">
        <div className="group/text flex size-full gap-2 p-4 max-lg:max-h-[140px] max-lg:min-h-[140px]">
          <div className="flex w-full flex-col gap-2">
            <p className="text-tg-body-l text-black">{content.product_title}</p>
            <p className="text-tg-body-m text-moss-grey-400">{content.product_description}</p>
          </div>
        </div>
        <div className="flex h-full items-end max-md:min-h-[180px]">
          <picture className="size-full lg:min-h-[220px]">
            {isFilled.imageThumbnail(content.asset.Mobile) && (
              <source
                media="(max-width: 768px)"
                srcSet={content.asset.Mobile.url + '?auto=compress,format&fit=max&w=768'}
                sizes="50vw"
              />
            )}
            <PrismicNextImage
              sizes="(max-width: 768px) 100vw, 1200px"
              field={content.asset}
              className="size-full object-cover transition-transform duration-200 group-hover/card:scale-[1.01]"
            />
          </picture>
        </div>
      </div>
    </Link>
  )
}
